import { initializeDB } from '../config/db';
import {
    TIMESHEET_RECORD_STORE,
    DAILY_JOB_NOTE_STORE,
    SIGNED_TIMESHEET_STORE,
    SELECTED_EMPLOYEES_STORE,
    SELECTED_JOBS_STORE,
    EMPLOYEE_STORE,
    JOB_STORE,
    ALT_JOB_STORE,
    TEMPS_STORE,
} from '../config/constants';
import { generateUUID } from '../utils/uuid';

// ─── Константы статусов синхронизации ────────────────────────────────────────

export const SYNC_STATUS = {
    PENDING: 'pending',   // локальная запись, ожидает отправки
    SYNCING: 'syncing',   // в процессе отправки
    SYNCED: 'synced',     // успешно отправлена на сервер
    ERROR: 'error',       // ошибка отправки, превышен лимит retry
    CONFLICT: 'conflict', // конфликт версий, требует ручного разрешения
};

export const MAX_SYNC_RETRIES = 5;
export const MAX_DAILY_HOURS = 24;

/** Событие, которое шлёт каждый save/delete таймшит-сущности. Слушает
 *  timesheetSyncService и триггерит sync немедленно (с debounce) —
 *  так запись уходит на сервер сразу после действия пользователя,
 *  не ждёт следующего 15-секундного tick-а. */
export const EVT_TIMESHEET_CHANGED = 'timesheetRecordChanged';

function dispatchChanged(clientId, entity = 'record') {
    if (typeof window === 'undefined') return;
    try {
        window.dispatchEvent(new CustomEvent(EVT_TIMESHEET_CHANGED, {
            detail: { clientId, entity },
        }));
    } catch {
        // ignore — не критично
    }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function generateClientId() {
    return generateUUID();
}

/**
 * Метаданные текущего пользователя, который вносит записи на iPad.
 * Читается из localStorage (заполняется при логине в LoginPage).
 * Эти поля уезжают в каждую запись — сервер сможет идентифицировать автора.
 */
function getCurrentUserMeta() {
    const userId = localStorage.getItem('userId');
    const employeeId = localStorage.getItem('employeeId');
    const fullName = localStorage.getItem('employeeFullName')
        || `${localStorage.getItem('firstName') || ''} ${localStorage.getItem('lastName') || ''}`.trim();

    return {
        userId: userId ? parseInt(userId, 10) : null,
        employeeId: employeeId ? parseInt(employeeId, 10) : null,
        fullName: fullName || null,
    };
}

/**
 * YYYY-MM-DD в локальной таймзоне (НЕ UTC).
 * CRITICAL: new Date().toISOString() использует UTC — неверно после 7pm EST!
 */
function toLocalDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getWeekDates(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + mondayOffset);

    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        dates.push(toLocalDateString(d));
    }
    return dates;
}

function getWeekEndingDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDay();
    const diff = day === 0 ? 0 : 7 - day;
    date.setDate(date.getDate() + diff);
    return toLocalDateString(date);
}

function getWeekNumber(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// ─── Валидация ──────────────────────────────────────────────────────────────

/**
 * Валидация структуры записи. Возвращает массив ошибок (пусто = ок).
 * Не проверяет лимит 24ч — это делается отдельно через ensureDailyHoursLimit().
 */
function validateTimesheetRecord(record) {
    const errors = [];

    // Temp — отрицательный employeeId (-tempId); на сервер уходит как tempId.
    if (record.employeeId == null || !Number.isFinite(Number(record.employeeId)) || Number(record.employeeId) === 0) {
        errors.push('Invalid employeeId');
    }
    if (record.jobId == null || !Number.isFinite(Number(record.jobId)) || Number(record.jobId) <= 0) {
        errors.push('Invalid jobId');
    }
    if (!record.workDate || !/^\d{4}-\d{2}-\d{2}$/.test(record.workDate)) {
        errors.push('Invalid workDate (expected YYYY-MM-DD)');
    }
    const hoursNum = Number(record.hours);
    if (!Number.isFinite(hoursNum) || hoursNum < 0 || hoursNum > MAX_DAILY_HOURS) {
        errors.push(`Hours must be between 0 and ${MAX_DAILY_HOURS}`);
    }
    if (record.notes != null && typeof record.notes !== 'string') {
        errors.push('notes must be a string');
    }
    if (!record.clientId || typeof record.clientId !== 'string') {
        errors.push('clientId is required');
    }

    return errors;
}

/**
 * Проверка лимита 24 часа в день ДЛЯ КОНКРЕТНОГО СОТРУДНИКА
 * по ВСЕМ его работам за указанную дату.
 *
 * Важно: бэкенд тоже должен делать эту проверку, т.к. один сотрудник
 * может быть назначен на разные работы через разные iPad одновременно.
 *
 * @returns {Promise<{ok: boolean, currentTotal: number, wouldBe: number}>}
 */
async function ensureDailyHoursLimit({ employeeId, workDate, hours, excludeClientId = null }) {
    const dayRecords = await getTimesheetRecordsByDate(workDate);
    const otherHours = dayRecords
        .filter(r => r.employeeId === employeeId)
        .filter(r => !r.isDeleted)
        .filter(r => r.clientId !== excludeClientId)
        .reduce((sum, r) => sum + (Number(r.hours) || 0), 0);

    const wouldBe = otherHours + (Number(hours) || 0);
    return {
        ok: wouldBe <= MAX_DAILY_HOURS,
        currentTotal: otherHours,
        wouldBe,
    };
}

// ─── TimesheetRecord CRUD ───────────────────────────────────────────────────

/**
 * Авто-заполняет sync-поля и метаданные автора.
 * Если поля уже есть — сохраняются (для обновлений с сервера).
 */
function withSyncDefaults(record, { isUpdate = false } = {}) {
    const now = new Date().toISOString();
    const user = getCurrentUserMeta();

    // Нормализуем legacy 'pending' → 'draft' (см. sync-сервис на сервере).
    // 'signed' — для SignedTimesheet (подпись бригадира), 'rejected' — для
    // TimesheetRecord (админ отклонил). Без них withSyncDefaults молча
    // ронял эти статусы в 'draft', и подписанная неделя выглядела как
    // неподписанная.
    const allowedStatuses = ['draft', 'signed', 'submitted', 'approved', 'rejected'];
    if (record.status != null && !allowedStatuses.includes(record.status)) {
        record = { ...record, status: 'draft' };
    }

    const merged = {
        // Sync-поля — без них нельзя синхронизироваться
        serverId: record.serverId ?? null,
        syncStatus: record.syncStatus ?? SYNC_STATUS.PENDING,
        syncErrorMessage: record.syncErrorMessage ?? null,
        syncRetryCount: record.syncRetryCount ?? 0,
        lastSyncAttempt: record.lastSyncAttempt ?? null,

        // Soft delete
        isDeleted: record.isDeleted ?? false,
        deletedAt: record.deletedAt ?? null,

        // Автор — ставится один раз при создании, дальше не трогаем
        createdByUserId: record.createdByUserId ?? user.userId,
        createdByEmployeeId: record.createdByEmployeeId ?? user.employeeId,
        createdByName: record.createdByName ?? user.fullName,

        // Оригинальные поля записи
        ...record,

        // Поля, которые ВСЕГДА перетираются при каждом save
        updatedAt: now,
        updatedByUserId: user.userId,
        updatedByName: user.fullName,
        localVersion: (record.localVersion ?? 0) + 1,
        createdAt: record.createdAt ?? now,
    };

    // Синтетический индекс для быстрой проверки лимита по сотруднику за день
    if (merged.employeeId != null && merged.workDate) {
        merged.employeeDate = `${merged.employeeId}|${merged.workDate}`;
    }

    // Когда запись уже была синхронизирована и клиент её редактирует —
    // помечаем как pending, чтобы очередь отправила апдейт.
    if (isUpdate && merged.syncStatus === SYNC_STATUS.SYNCED) {
        merged.syncStatus = SYNC_STATUS.PENDING;
        merged.syncRetryCount = 0;
        merged.syncErrorMessage = null;
    }

    return merged;
}

async function saveTimesheetRecord(record, { validateLimit = true } = {}) {
    const prepared = withSyncDefaults(record, { isUpdate: Boolean(record.updatedAt) });

    const errors = validateTimesheetRecord(prepared);
    if (errors.length > 0) {
        throw new Error(`Validation failed: ${errors.join(', ')}`);
    }

    if (validateLimit && !prepared.isDeleted && prepared.hours > 0) {
        const check = await ensureDailyHoursLimit({
            employeeId: prepared.employeeId,
            workDate: prepared.workDate,
            hours: prepared.hours,
            excludeClientId: prepared.clientId,
        });
        if (!check.ok) {
            const err = new Error(
                `Daily limit ${MAX_DAILY_HOURS}h exceeded: already ${check.currentTotal}h, trying to add ${prepared.hours}h`
            );
            err.code = 'DAILY_LIMIT_EXCEEDED';
            err.details = check;
            throw err;
        }
    }

    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readwrite');
    await tx.store.put(prepared);
    await tx.done;
    dispatchChanged(prepared.clientId, 'record');
    return prepared;
}

async function getTimesheetRecordsByDate(date) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readonly');
    const index = tx.store.index('byDate');
    const records = await index.getAll(date);
    await tx.done;
    return records;
}

async function getTimesheetRecordsByWeek(dateInWeek) {
    const weekDates = getWeekDates(dateInWeek);
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readonly');
    const index = tx.store.index('byDate');
    const allRecords = [];
    for (const d of weekDates) {
        const recs = await index.getAll(d);
        allRecords.push(...recs);
    }
    await tx.done;
    return allRecords;
}

async function getTimesheetRecordsByDateRange(startDate, endDate) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readonly');
    const index = tx.store.index('byDate');
    const range = IDBKeyRange.bound(startDate, endDate);
    const records = await index.getAll(range);
    await tx.done;
    return records;
}

async function getTimesheetRecordById(clientId) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readonly');
    const rec = await tx.store.get(clientId);
    await tx.done;
    return rec || null;
}

/**
 * Soft delete: помечает запись isDeleted=true и ставит syncStatus=pending,
 * чтобы очередь отправила DELETE на сервер.
 * Если запись не была синхронизирована (нет serverId) — удаляем физически,
 * чтобы не мусорить в IndexedDB.
 */
async function deleteTimesheetRecord(clientId) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readwrite');
    const existing = await tx.store.get(clientId);

    if (!existing) {
        await tx.done;
        return;
    }

    if (!existing.serverId) {
        // Никогда не было на сервере — можно удалить физически
        await tx.store.delete(clientId);
    } else {
        // Есть на сервере — soft delete для последующей синхронизации
        const user = getCurrentUserMeta();
        const now = new Date().toISOString();
        await tx.store.put({
            ...existing,
            isDeleted: true,
            deletedAt: now,
            updatedAt: now,
            updatedByUserId: user.userId,
            updatedByName: user.fullName,
            syncStatus: SYNC_STATUS.PENDING,
            syncRetryCount: 0,
            syncErrorMessage: null,
            localVersion: (existing.localVersion ?? 1) + 1,
        });
    }

    await tx.done;
    dispatchChanged(clientId, 'record');
}

/**
 * Физическое удаление (только для очистки после подтверждённой синхронизации).
 */
async function hardDeleteTimesheetRecord(clientId) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readwrite');
    await tx.store.delete(clientId);
    await tx.done;
}

/**
 * Все записи, ожидающие синхронизации.
 * Используется будущим timesheetSyncService.
 */
async function getPendingSyncRecords(limit = 100) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readonly');
    const index = tx.store.index('bySyncStatus');
    const pending = await index.getAll(SYNC_STATUS.PENDING, limit);
    await tx.done;
    return pending;
}

/**
 * Универсальный чтение по sync-статусу через индекс. Используется для UI-бейджа
 * (errorCount/conflictCount) и для heal-цикла в timesheetSyncService.
 */
async function getRecordsBySyncStatus(status, storeName = TIMESHEET_RECORD_STORE, limit = 10000) {
    const db = await initializeDB();
    const tx = db.transaction(storeName, 'readonly');
    const index = tx.store.index('bySyncStatus');
    const rows = await index.getAll(status, limit);
    await tx.done;
    return rows;
}

/**
 * "Heal" — перевод записей из ERROR → PENDING чтобы авто-sync подхватил их заново.
 * Инкрементирует globalErrorCount — счётчик "дошли до error за всё время".
 * Если запись падает > HEAL_MAX_CYCLES раз — heal её пропускает (она явно битая,
 * нужно ручное вмешательство через UI).
 *
 * Работает во всех трёх stores: records / dailyNotes / signedTimesheets.
 *
 * @param {object} [options]
 * @param {number} [options.maxCycles=10]  — сколько раз готовы воскрешать одну и ту же
 * @returns {Promise<{ reset: number, skipped: number, stores: Record<string, number> }>}
 */
async function resetErrorsToRetry({ maxCycles = 10 } = {}) {
    const db = await initializeDB();
    let reset = 0;
    let skipped = 0;
    const stores = {};

    for (const storeName of [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE]) {
        if (!db.objectStoreNames.contains(storeName)) continue;
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        if (!store.indexNames.contains('bySyncStatus')) {
            await tx.done;
            continue;
        }
        const errorRows = await store.index('bySyncStatus').getAll(SYNC_STATUS.ERROR);
        let perStore = 0;
        for (const r of errorRows) {
            const cycles = (r.globalErrorCount || 0) + 1;
            if (cycles > maxCycles) {
                skipped++;
                continue;
            }
            r.syncStatus = SYNC_STATUS.PENDING;
            r.syncRetryCount = 0;
            r.syncErrorMessage = null;
            r.globalErrorCount = cycles;
            await store.put(r);
            perStore++;
            reset++;
        }
        await tx.done;
        if (perStore > 0) stores[storeName] = perStore;
    }

    return { reset, skipped, stores };
}

/**
 * Ручной retry для конкретного clientId — использует UI кнопка "Retry this".
 */
async function resetOneToRetry(clientId, storeName = TIMESHEET_RECORD_STORE) {
    const db = await initializeDB();
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const r = await store.get(clientId);
    if (!r) {
        await tx.done;
        return false;
    }
    r.syncStatus = SYNC_STATUS.PENDING;
    r.syncRetryCount = 0;
    r.syncErrorMessage = null;
    await store.put(r);
    await tx.done;
    return true;
}

// ─── DailyJobNote CRUD ─────────────────────────────────────────────────────

async function saveDailyJobNote(note) {
    const prepared = withSyncDefaults(note, { isUpdate: Boolean(note.updatedAt) });
    if (!prepared.clientId) {
        throw new Error('clientId is required for DailyJobNote');
    }
    if (!prepared.noteDate || !/^\d{4}-\d{2}-\d{2}$/.test(prepared.noteDate)) {
        throw new Error('Invalid noteDate (expected YYYY-MM-DD)');
    }
    const db = await initializeDB();
    const tx = db.transaction(DAILY_JOB_NOTE_STORE, 'readwrite');
    await tx.store.put(prepared);
    await tx.done;
    dispatchChanged(prepared.clientId, 'dailyNote');
    return prepared;
}

async function getDailyJobNotesByDate(date) {
    const db = await initializeDB();
    const tx = db.transaction(DAILY_JOB_NOTE_STORE, 'readonly');
    const index = tx.store.index('byDate');
    const notes = await index.getAll(date);
    await tx.done;
    return notes;
}

async function getDailyJobNotesByWeek(dateInWeek) {
    const weekDates = getWeekDates(dateInWeek);
    const db = await initializeDB();
    const tx = db.transaction(DAILY_JOB_NOTE_STORE, 'readonly');
    const index = tx.store.index('byDate');
    const allNotes = [];
    for (const d of weekDates) {
        const notes = await index.getAll(d);
        allNotes.push(...notes);
    }
    await tx.done;
    return allNotes;
}

async function deleteDailyJobNote(clientId) {
    const db = await initializeDB();
    const tx = db.transaction(DAILY_JOB_NOTE_STORE, 'readwrite');
    const existing = await tx.store.get(clientId);
    if (!existing) {
        await tx.done;
        return;
    }
    if (!existing.serverId) {
        await tx.store.delete(clientId);
    } else {
        const user = getCurrentUserMeta();
        const now = new Date().toISOString();
        await tx.store.put({
            ...existing,
            isDeleted: true,
            deletedAt: now,
            updatedAt: now,
            updatedByUserId: user.userId,
            updatedByName: user.fullName,
            syncStatus: SYNC_STATUS.PENDING,
            syncRetryCount: 0,
        });
    }
    await tx.done;
    dispatchChanged(clientId, 'dailyNote');
}

// ─── SignedTimesheet CRUD ───────────────────────────────────────────────────

async function saveSignedTimesheet(signed) {
    const prepared = withSyncDefaults(signed, { isUpdate: Boolean(signed.updatedAt) });
    if (!prepared.clientId) {
        throw new Error('clientId is required for SignedTimesheet');
    }
    if (!prepared.weekEndingDate) {
        throw new Error('weekEndingDate is required');
    }
    const db = await initializeDB();
    const tx = db.transaction(SIGNED_TIMESHEET_STORE, 'readwrite');
    await tx.store.put(prepared);
    await tx.done;
    dispatchChanged(prepared.clientId, 'signed');
    return prepared;
}

async function getAllSignedTimesheets() {
    const db = await initializeDB();
    const tx = db.transaction(SIGNED_TIMESHEET_STORE, 'readonly');
    const all = await tx.store.getAll();
    await tx.done;
    return all;
}

async function getSignedTimesheetsByWeek(weekEndingDate) {
    const all = await getAllSignedTimesheets();
    return all.filter(s => s.weekEndingDate === weekEndingDate && !s.isDeleted);
}

/**
 * Оптимистично проставить status='signed' для всех timesheet_record за указанную
 * неделю+job. Используется в signTimesheet чтобы UI получил signed-флаг сразу,
 * не дожидаясь pull от сервера (15-секундный round-trip).
 *
 * Сервер всё равно сделает то же через propagateSignedStatusToRecords при
 * следующем push — pull-merge безопасно подтвердит наши локальные изменения.
 *
 * Не меняет syncStatus: эти записи уже синхронизированы с сервера, мы просто
 * UI-флаг проставляем локально вперёд серверного round-trip.
 *
 * Возвращает число обновлённых записей.
 */
async function markRecordsSignedForJobWeek(jobId, weekEndingDate) {
    const weekDates = getWeekDates(weekEndingDate);
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readwrite');
    const idx = tx.store.index('byDate');
    const allowedPriorStatuses = new Set(['draft', 'submitted', 'rejected']);
    let updated = 0;
    for (const d of weekDates) {
        const recs = await idx.getAll(d);
        for (const r of recs) {
            if (r.jobId !== jobId) continue;
            if (r.isDeleted) continue;
            if (!allowedPriorStatuses.has(r.status)) continue;
            await tx.store.put({ ...r, status: 'signed' });
            updated++;
        }
    }
    await tx.done;
    return updated;
}

// ─── Selected Employees ─────────────────────────────────────────────────────

async function saveSelectedEmployees(date, employeeIds) {
    const db = await initializeDB();
    const tx = db.transaction(SELECTED_EMPLOYEES_STORE, 'readwrite');
    await tx.store.put({ date, employeeIds });
    await tx.done;
}

async function saveSelectedEmployeesForWeek(dateInWeek, employeeIds) {
    const weekDates = getWeekDates(dateInWeek);
    const db = await initializeDB();
    const tx = db.transaction(SELECTED_EMPLOYEES_STORE, 'readwrite');
    for (const d of weekDates) {
        await tx.store.put({ date: d, employeeIds });
    }
    await tx.done;
}

async function getSelectedEmployeesWithFallback(date) {
    const db = await initializeDB();
    const tx = db.transaction(SELECTED_EMPLOYEES_STORE, 'readonly');
    const record = await tx.store.get(date);
    await tx.done;

    if (record?.employeeIds?.length > 0) return record.employeeIds;

    const weekDates = getWeekDates(date);
    const tx2 = db.transaction(SELECTED_EMPLOYEES_STORE, 'readonly');
    for (const d of weekDates) {
        if (d === date) continue;
        const rec = await tx2.store.get(d);
        if (rec?.employeeIds?.length > 0) {
            await tx2.done;
            return rec.employeeIds;
        }
    }
    await tx2.done;
    return [];
}

/**
 * Все subject-id (сотрудники + temps) из ЛОКАЛЬНОГО ростера — объединение по всем
 * сохранённым дням/неделям устройства. Нужно, чтобы сузить incremental-pull: тянем
 * серверные обновления только по СВОИМ людям (включая чужие часы по ним), а не по
 * всей компании. temps на клиенте с negative id → возвращаем как отдельный tempIds.
 * Пусто → вызывающий не ставит фильтр (fallback к прежнему поведению).
 */
async function getRosterSubjectIds(weeksBack = 2) {
    const db = await initializeDB();
    const all = await db.getAll(SELECTED_EMPLOYEES_STORE);
    // Только те, кто РЕАЛЬНО в списке сейчас: 2 недели (текущая + прошлая — запас на
    // стык недель, пока лист новой недели ещё не скопирован). Шире брать нельзя —
    // тогда на устройство сыпались бы записи людей, которых в листе давно нет
    // (юзер: «попадать должно только если он есть в списке»). Старые недели при
    // открытии подтянет week-mode pull со своим ростером.
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - weeksBack * 7);
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
    const set = new Set();
    for (const row of all) {
        if (row.date && row.date < cutoffStr) continue;
        for (const id of (row.employeeIds || [])) set.add(id);
    }
    const employeeIds = [];
    const tempIds = [];
    for (const id of set) {
        if (id > 0) employeeIds.push(id);
        else if (id < 0) tempIds.push(-id);
    }
    return { employeeIds, tempIds };
}

// ─── Selected Jobs ──────────────────────────────────────────────────────────

async function saveSelectedJobs(date, jobIds) {
    const db = await initializeDB();
    const tx = db.transaction(SELECTED_JOBS_STORE, 'readwrite');
    await tx.store.put({ date, jobIds });
    await tx.done;
}

async function saveSelectedJobsForWeek(dateInWeek, jobIds) {
    const weekDates = getWeekDates(dateInWeek);
    const db = await initializeDB();
    const tx = db.transaction(SELECTED_JOBS_STORE, 'readwrite');
    for (const d of weekDates) {
        await tx.store.put({ date: d, jobIds });
    }
    await tx.done;
}

async function getSelectedJobsWithFallback(date) {
    const db = await initializeDB();
    const tx = db.transaction(SELECTED_JOBS_STORE, 'readonly');
    const record = await tx.store.get(date);
    await tx.done;

    if (record?.jobIds?.length > 0) return record.jobIds;

    const weekDates = getWeekDates(date);
    const tx2 = db.transaction(SELECTED_JOBS_STORE, 'readonly');
    for (const d of weekDates) {
        if (d === date) continue;
        const rec = await tx2.store.get(d);
        if (rec?.jobIds?.length > 0) {
            await tx2.done;
            return rec.jobIds;
        }
    }
    await tx2.done;
    return [];
}

// ─── Reference data ─────────────────────────────────────────────────────────

async function getAllEmployees() {
    const db = await initializeDB();
    const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
    const all = await tx.store.getAll();
    await tx.done;
    return all;
}

/**
 * Temps в форме строки грида: id = -tempId, как их хранят списки бригады и
 * записи. Без этого после перезагрузки выбранный temp не находил карточку и
 * молча пропадал из таблицы.
 */
async function getAllTemps() {
    const db = await initializeDB();
    if (!db.objectStoreNames.contains(TEMPS_STORE)) return [];
    const tx = db.transaction(TEMPS_STORE, 'readonly');
    const all = await tx.store.getAll();
    await tx.done;
    return all.map(t => {
        const name = t.FullName || t.fullName || 'Unknown';
        return { ...t, id: -Math.abs(Number(t.id)), tempId: t.id, fullName: name, FullName: name, _type: 'temp' };
    });
}

/**
 * Работы таймшита: сначала alternative jobs (Training, Overhead, TRNG - NCCER…),
 * потом обычные. Alt — как в десктопном шаблоне таймшита: только с Seq > 0, в
 * порядке Seq, подпись — anAlias, если задан. Часы пишутся на job-строку, которую
 * сервер завёл под этот код (jobId), — дальше это обычная работа.
 */
async function getAllJobs() {
    const db = await initializeDB();
    const tx = db.transaction(JOB_STORE, 'readonly');
    const jobs = await tx.store.getAll();
    await tx.done;

    let alt = [];
    if (db.objectStoreNames.contains(ALT_JOB_STORE)) {
        const altTx = db.transaction(ALT_JOB_STORE, 'readonly');
        alt = await altTx.store.getAll();
        await altTx.done;
    }

    const byId = new Map(jobs.map(j => [j.id, j]));
    const altJobs = alt
        .filter(a => a.jobId && a.Seq > 0)
        .sort((a, b) => a.Seq - b.Seq || String(a.Job).localeCompare(String(b.Job)))
        .map(a => ({
            // Код заведён и в основном справочнике — берём его карточку целиком.
            ...(byId.get(a.jobId) || {
                id: a.jobId,
                Job: (a.anAlias || '').trim() || a.Job,
                JobName: a.Comment || '',
                location: a.Location || '',
            }),
            isAltJob: true,
            altSeq: a.Seq,
        }));
    const altIds = new Set(altJobs.map(j => j.id));

    // Коды работы (7888-1, 7888-2…) — сразу под своей работой, по номеру кода.
    const regular = jobs.filter(j => !altIds.has(j.id));
    const subsOf = new Map();
    for (const j of regular) {
        if (!j.parentJobId) continue;
        if (!subsOf.has(j.parentJobId)) subsOf.set(j.parentJobId, []);
        subsOf.get(j.parentJobId).push(j);
    }
    for (const list of subsOf.values()) {
        list.sort((a, b) => String(a.jobCodeNo).localeCompare(String(b.jobCodeNo), undefined, { numeric: true }));
    }
    const ordered = [];
    const placed = new Set();
    for (const j of regular) {
        if (j.parentJobId) continue;
        ordered.push(j);
        for (const s of subsOf.get(j.id) || []) { ordered.push(s); placed.add(s.id); }
    }
    // Код, чья работа сейчас не активна, — всё равно в списке, в конце.
    for (const j of regular) if (j.parentJobId && !placed.has(j.id)) ordered.push(j);

    return [...altJobs, ...ordered];
}

/**
 * Только что созданный код работы (7888-1) — сразу в локальный справочник
 * работ, не дожидаясь полной синхронизации справочников.
 */
async function putJob(job) {
    if (!job || !job.id) return;
    const db = await initializeDB();
    const tx = db.transaction(JOB_STORE, 'readwrite');
    await tx.store.put(job);
    await tx.done;
}

// ─── Вошедший пользователь в листе ──────────────────────────────────────────

/**
 * Карточка вошедшего среди сотрудников. /api/user отдаёт внешний номер
 * (employeeId = EE), а строки грида — по локальному id карточки, поэтому
 * ищем по EE, запасной вариант — по ФИО.
 */
function findSelfEmployee(employees) {
    const ee = (localStorage.getItem('employeeId') || '').trim();
    const name = (localStorage.getItem('employeeFullName') || '').trim().toLowerCase();
    if (!ee && !name) return null;
    return (ee && employees.find(e => e.id > 0 && String(e.employeeId ?? '').trim() === ee))
        || (name && employees.find(e => e.id > 0 && getEmployeeName(e).trim().toLowerCase() === name))
        || null;
}

// ─── Field normalization helpers ────────────────────────────────────────────

function getEmployeeName(emp) {
    return emp?.fullName || emp?.FullName || emp?.name || '';
}

function getJobName(job) {
    // У кода работы (7888-1) своё описание — его и показываем вместо названия работы.
    if (job?.parentJobId && job?.codeDescription) return job.codeDescription;
    return job?.JobName || job?.jobName || '';
}

function getJobCode(job) {
    return job?.Job || job?.jobCode || '';
}

function getJobCustomer(job) {
    return job?.Customer || job?.customer || '';
}

// ─── Export ─────────────────────────────────────────────────────────────────

const timesheetService = {
    // Helpers
    generateClientId,
    toLocalDateString,
    getWeekDates,
    getWeekEndingDate,
    getWeekNumber,
    getCurrentUserMeta,

    // Validation
    validateTimesheetRecord,
    ensureDailyHoursLimit,

    // TimesheetRecord
    saveTimesheetRecord,
    getTimesheetRecordsByDate,
    getTimesheetRecordsByWeek,
    getTimesheetRecordsByDateRange,
    getTimesheetRecordById,
    deleteTimesheetRecord,
    hardDeleteTimesheetRecord,
    getPendingSyncRecords,
    getRecordsBySyncStatus,
    resetErrorsToRetry,
    resetOneToRetry,

    // DailyJobNote
    saveDailyJobNote,
    getDailyJobNotesByDate,
    getDailyJobNotesByWeek,
    deleteDailyJobNote,

    // SignedTimesheet
    saveSignedTimesheet,
    getAllSignedTimesheets,
    getSignedTimesheetsByWeek,
    markRecordsSignedForJobWeek,

    // Selected Employees
    saveSelectedEmployees,
    saveSelectedEmployeesForWeek,
    getSelectedEmployeesWithFallback,
    getRosterSubjectIds,

    // Selected Jobs
    saveSelectedJobs,
    saveSelectedJobsForWeek,
    getSelectedJobsWithFallback,

    // Reference data
    getAllEmployees,
    getAllTemps,
    getAllJobs,
    putJob,

    // Вошедший в листе
    findSelfEmployee,

    // Field normalization
    getEmployeeName,
    getJobName,
    getJobCode,
    getJobCustomer,
};

export default timesheetService;
